import { Check, ChevronDown, Search, X } from "lucide-react";
import * as React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface ComboboxOption {
  value: string;
  label: string;
  /** A second, quieter line in the list: a key, a type. */
  hint?: string;
  /** What the control shows beside the chosen option instead of the hint: where it is, for one. */
  detail?: string;
  /** Options sharing a group are listed under its heading, in the order given. */
  group?: string;
  /** Matched by the search too, never shown. */
  keywords?: string;
}

/**
 * Pick one thing from a long list: a trigger that says what is picked, a panel with a search, the
 * options grouped, and a way to pick nothing. The single-choice twin of `MultiSelect`, for lists too
 * long for a `Select` to scroll (a form's eighty fields) and where the search is how people find
 * things. Arrow keys move, Enter picks, Escape closes, and typing filters.
 *
 * With `onSearch` the server searches instead, as `MultiSelect` does: each keystroke is handed up and
 * `options` are taken as the matches, listed as given.
 */
export function Combobox({
  label, options, value, onChange, placeholder = "Choose", clearLabel = "None", emptyText = "Nothing to pick",
  disabled = false, invalid = false, modal = false, className, panelClassName, onSearch, searchedFor, picked,
  loading = false, footer,
}: {
  /** What is being picked, for the search box and screen readers. */
  label: string;
  options: readonly ComboboxOption[];
  value: string | null;
  onChange: (next: string | null) => void;
  placeholder?: string;
  /** The choice that clears the value; absent when nothing is picked. */
  clearLabel?: string | null;
  emptyText?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** Inside a dialog: the dialog's scroll lock swallows the wheel outside its own tree, and the panel is
   *  portalled out of it, so the panel takes the lock itself while it is open. */
  modal?: boolean;
  className?: string;
  panelClassName?: string;
  /** The server searches: called with what is typed, and with "" when the panel closes. */
  onSearch?: (query: string) => void;
  /** What `options` were searched for. A parent that debounces searches a moment after the typing
   *  stops, and until it catches up, the options are narrowed here as well: the list, and Enter,
   *  must not offer rows from before the search. */
  searchedFor?: string;
  /** The picked option, for naming it when the matches no longer include it. */
  picked?: ComboboxOption | null;
  /** The matches are on their way: the empty list says so rather than "No match". */
  loading?: boolean;
  /** Under the list: how many there are, and a way to load more. */
  footer?: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(false);
  const chosen = value === null
    ? undefined
    : (options.find((o) => o.value === value) ?? (picked?.value === value ? picked : undefined));
  React.useEffect(() => {
    // The panel's list starts each opening with an empty search of its own; the server's search
    // is cleared as the panel closes.
    if (!open) onSearch?.("");
    // Only on opening and closing: a parent's new `onSearch` each render is not a reason to clear.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={modal}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label={`${label}: ${chosen?.label ?? placeholder}`}
          aria-invalid={invalid || undefined}
          disabled={disabled}
          className={cn(
            "flex h-8 w-full min-w-0 items-center gap-2 rounded-md border bg-surface px-2.5 text-left text-sm shadow-xs transition-colors",
            "focus-visible:border-accent-8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/25",
            "disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-text-muted",
            invalid ? "border-danger-9" : "border-border hover:bg-surface-hover",
            className,
          )}
        >
          <span className={cn("min-w-0 flex-1 truncate", chosen ? "text-text" : "text-text-subtle")}>
            {chosen?.label ?? placeholder}
          </span>
          {chosen && (chosen.detail ?? chosen.hint) && (
            <span className="hidden max-w-[45%] truncate font-mono text-2xs text-text-subtle sm:inline">{chosen.detail ?? chosen.hint}</span>
          )}
          <ChevronDown className="size-3.5 shrink-0 text-text-subtle" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent className={cn("w-[var(--radix-popover-trigger-width)] min-w-72 p-0", panelClassName)} align="start">
        <ComboboxList
          label={label}
          options={options}
          value={value}
          clearLabel={clearLabel}
          emptyText={emptyText}
          onSearch={onSearch}
          searchedFor={searchedFor}
          loading={loading}
          onPick={(next) => {
            onChange(next);
            setOpen(false);
          }}
        />
        {footer && <div className="border-t border-border px-3 py-1.5">{footer}</div>}
      </PopoverContent>
    </Popover>
  );
}

/**
 * The combobox's panel on its own: the search, the grouped list that scrolls under it, and the
 * row that picks nothing. For a picker opened from a control of another shape, such as a word in
 * a cadence's sentence. Mounted while it is shown, so each opening starts with an empty search.
 */
export function ComboboxList({
  label, options, value, onPick, clearLabel = "None", emptyText = "Nothing to pick", plainHints = false, footer, className,
  listClassName = "max-h-72", onSearch, searchedFor, loading = false,
}: {
  /** What is being picked, for the search box and screen readers. */
  label: string;
  options: readonly ComboboxOption[];
  value: string | null;
  /** Called with the picked value, or null for the row that picks nothing. */
  onPick: (next: string | null) => void;
  /** The choice that clears the value; absent when nothing is picked. */
  clearLabel?: string | null;
  emptyText?: string;
  /** Hints as words that wrap, rather than a key in small type: what an outcome does, for one. */
  plainHints?: boolean;
  /** A line under the list: what is left out of it, and why. */
  footer?: React.ReactNode;
  className?: string;
  /** How tall the list may grow before it scrolls. */
  listClassName?: string;
  /** The server searches: called with what is typed (`Combobox`'s, which also clears it on close). */
  onSearch?: (query: string) => void;
  /** What `options` were searched for: until a debounced search catches up, they are narrowed here
   *  as well, so the list and Enter offer no rows from before the search. */
  searchedFor?: string;
  /** The matches are on their way: the empty list says so rather than "No match". */
  loading?: boolean;
}) {
  const [query, setQuery] = React.useState("");
  const [cursor, setCursor] = React.useState(0);
  const listRef = React.useRef<HTMLUListElement>(null);
  // The search box keeps the focus while the arrows move the highlight, so it names the highlighted
  // option (`aria-activedescendant`) for a screen reader to say, by the id each option carries.
  const listId = React.useId();
  const optionId = (index: number) => `${listId}-option-${index}`;
  const needle = query.trim().toLowerCase();
  const behind = onSearch !== undefined && searchedFor !== undefined && searchedFor !== query.trim();
  const visible = needle && (!onSearch || behind)
    ? options.filter((o) => `${o.label} ${o.hint ?? ""} ${o.keywords ?? ""} ${o.value}`.toLowerCase().includes(needle))
    : options;
  // "None" is a row of its own at the top, so the cursor counts it when it is there.
  const clearable = clearLabel !== null && value !== null && !needle;
  const rows = clearable ? visible.length + 1 : visible.length;
  // The highlighted row, held within the rows there are now: the options can change under an open
  // picker (the contact form read again), and the highlight, Enter and what a screen reader hears
  // all go by this one index. -1 when there is no row.
  const active = rows > 0 ? Math.max(0, Math.min(cursor, rows - 1)) : -1;

  const search = (next: string) => {
    setQuery(next);
    onSearch?.(next);
  };
  React.useEffect(() => setCursor(0), [needle]);
  React.useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const pickCursor = () => {
    if (active < 0) return;
    if (clearable && active === 0) return onPick(null);
    const option = visible[clearable ? active - 1 : active];
    if (option) onPick(option.value);
  };

  let lastGroup: string | undefined;
  return (
    <div className={cn("flex min-h-0 flex-col", className)}>
      <div className="shrink-0 border-b border-border p-2">
        <label className="flex h-8 items-center gap-2 rounded-md border border-border bg-surface-sunken px-2">
          <Search className="size-3.5 text-text-subtle" aria-hidden />
          <input
            autoFocus
            role="combobox"
            aria-expanded
            aria-autocomplete="list"
            aria-controls={listId}
            aria-activedescendant={active >= 0 ? optionId(active) : undefined}
            value={query}
            onChange={(e) => search(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                if (rows > 0) setCursor(Math.min(rows - 1, active + 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                if (rows > 0) setCursor(Math.max(0, active - 1));
              } else if (e.key === "Enter") {
                e.preventDefault();
                pickCursor();
              }
            }}
            placeholder={`Search ${label.toLowerCase()}`}
            aria-label={`Search ${label.toLowerCase()}`}
            className="min-w-0 flex-1 bg-transparent text-sm text-text outline-none placeholder:text-text-subtle"
          />
          {query && (
            <button type="button" onClick={() => search("")} aria-label="Clear the search" className="text-text-subtle hover:text-text">
              <X className="size-3.5" />
            </button>
          )}
        </label>
      </div>
      <ul ref={listRef} id={listId} role="listbox" aria-label={label} className={cn("min-h-0 overflow-y-auto p-1 scrollbar-subtle", listClassName)}>
        {clearable && (
          <li
            id={optionId(0)}
            role="option"
            aria-selected={false}
            data-index={0}
            tabIndex={-1}
            onPointerEnter={() => setCursor(0)}
            onClick={() => onPick(null)}
            className={cn(
              "flex cursor-default select-none items-center gap-2 rounded px-2 py-1.5 text-sm text-text-muted",
              active === 0 && "bg-surface-hover",
            )}
          >
            <X className="size-3.5 shrink-0 text-text-subtle" aria-hidden />
            {clearLabel}
          </li>
        )}
        {visible.length === 0 && (
          <li className="px-2 py-6 text-center text-xs text-text-subtle">{loading || behind ? "Searching…" : needle ? "No match" : emptyText}</li>
        )}
        {visible.map((o, i) => {
          const index = clearable ? i + 1 : i;
          const heading = o.group && o.group !== lastGroup ? o.group : null;
          lastGroup = o.group;
          const on = o.value === value;
          return (
            <React.Fragment key={o.value}>
              {heading && (
                <li role="presentation" className="px-2 pb-1 pt-2 text-2xs font-medium uppercase tracking-wide text-text-subtle">
                  {heading}
                </li>
              )}
              <li
                id={optionId(index)}
                role="option"
                aria-selected={on}
                data-index={index}
                tabIndex={-1}
                onPointerEnter={() => setCursor(index)}
                onClick={() => onPick(o.value)}
                className={cn(
                  "flex cursor-default select-none items-center gap-2 rounded px-2 py-1.5 text-sm",
                  index === active && "bg-surface-hover",
                )}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-text">{o.label}</span>
                  {o.hint && (
                    <span className={cn("block", plainHints ? "text-xs text-text-muted" : "truncate font-mono text-2xs text-text-subtle")}>
                      {o.hint}
                    </span>
                  )}
                </span>
                {on && <Check className="size-3.5 shrink-0 text-accent-11" aria-hidden />}
              </li>
            </React.Fragment>
          );
        })}
      </ul>
      {footer && <div className="shrink-0 border-t border-border px-3 py-2 text-xs text-text-subtle">{footer}</div>}
    </div>
  );
}
